import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Receipt, Wallet, CreditCard, CheckCircle2, XCircle, MessageCircle, Mail,
} from "lucide-react";

const PHONE_RAW = "447350695847";
const PHONE_DISPLAY = "+44 7350 695847";
const SUPPORT_EMAIL = "help_mrscashback@outlook.com";

export default function HowItWorks() {
  const navigate = useNavigate();
  const openWhatsApp = () => {
    window.open(`https://wa.me/${PHONE_RAW}`, "_blank", "noopener,noreferrer");
  };
  const openEmail = () => {
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=Mrs%20CashBack%20Support`;
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 12px 14px",
        borderBottom: "1px solid var(--border)",
        position: "sticky", top: 0, background: "var(--bg)", zIndex: 10,
        paddingTop: "max(16px, env(safe-area-inset-top))",
      }}>
        <button
          data-testid="how-back-button"
          onClick={() => navigate(-1)}
          style={{
            width: 44, height: 44, borderRadius: 22,
            background: "var(--surface-elev)", border: "1px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <ChevronLeft size={22} />
        </button>
        <div style={{ fontSize: 17, fontWeight: 700 }}>How It Works</div>
        <div style={{ width: 44 }} />
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "20px 18px 60px" }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1, margin: 0, lineHeight: 1.2 }}>
          Hi, welcome to<br />
          <span style={{ color: "var(--primary)" }}>Mrs CashBack!</span>
        </h1>
        <p style={{ color: "var(--text-secondary)", fontSize: 15, marginTop: 10, marginBottom: 22 }}>
          Here's how it works:
        </p>

        <Step n="1" Icon={Receipt} title="Send your receipts 🧾"
          body="Upload a clear photo of your receipt on the home screen. Please note: to qualify, receipts must have a total of £9 or more." />
        <Step n="2" Icon={Wallet} title="Get cash instantly 💷"
          body="Every approved receipt drops between £1.60 and £3.40 straight into your wallet — the amount depends on your total spend." />

        <div data-testid="eligible-card" style={{
          display: "flex", gap: 12,
          background: "#1FAE5E", border: "1px solid #1FAE5E",
          borderRadius: 28, padding: 16, marginBottom: 12,
          boxShadow: "0 14px 28px -20px rgba(31, 174, 94, 0.55)",
        }}>
          <CheckCircle2 size={20} color="#FFFFFF" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: "#FFFFFF", fontSize: 14, fontWeight: 800 }}>Eligible Receipts</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4, color: "#FFFFFF" }}>
              McDonald's, KFC and Popeyes only.
            </div>
            <div style={{ color: "rgba(255,255,255,0.92)", fontSize: 12, fontWeight: 600, marginTop: 4 }}>
              More places being added soon
            </div>
          </div>
        </div>

        <div data-testid="ineligible-card" style={{
          display: "flex", gap: 12,
          background: "var(--error)", border: "1px solid var(--error)",
          borderRadius: 28, padding: 16, marginBottom: 12,
          boxShadow: "0 14px 28px -20px rgba(217, 72, 72, 0.55)",
        }}>
          <XCircle size={20} color="#FFFFFF" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: "#FFFFFF", fontSize: 14, fontWeight: 800, marginBottom: 4 }}>Not Eligible</div>
            <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.45, marginTop: 2, color: "#FFFFFF" }}>
              • Delivery / food-delivery receipts (e.g. Uber Eats, Just Eat, Deliveroo) are <b>not eligible</b>.
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.45, marginTop: 2, color: "#FFFFFF" }}>
              • Orders <b>more than 3 days old</b> are also not eligible.
            </div>
          </div>
        </div>

        <Step n="3" Icon={Wallet} title="Build your balance"
          body="Once your balance reaches £10 (£20 from your second payout onwards), you can click Payout to cash out." />
        <Step n="4" Icon={CreditCard} title="Get paid"
          body="We'll send your payment to your chosen payment method." />

        <div style={{
          display: "flex", gap: 14,
          background: "var(--surface-elev)", border: "1px solid var(--peach-border)",
          borderRadius: 28, padding: 18, marginTop: 16,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, background: "#25D366",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <MessageCircle size={22} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>Need help?</div>
            <div style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 4, lineHeight: 1.4 }}>
              If you ever have any issues, just reach out to us on WhatsApp or by email and our team will help.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
              <button
                data-testid="how-whatsapp-button"
                onClick={openWhatsApp}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "#25D366", color: "#fff",
                  borderRadius: 999, padding: "10px 16px",
                  fontWeight: 800, fontSize: 14,
                }}
              >
                <MessageCircle size={18} />
                {PHONE_DISPLAY}
              </button>
              <a
                data-testid="how-email-button"
                href={`mailto:${SUPPORT_EMAIL}?subject=Mrs%20CashBack%20Support`}
                onClick={(e) => { e.preventDefault(); openEmail(); }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "#0F8C4A", color: "#fff",
                  borderRadius: 999, padding: "10px 16px",
                  fontWeight: 800, fontSize: 13,
                  textDecoration: "none",
                  wordBreak: "break-all",
                }}
              >
                <Mail size={18} />
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
        </div>

        <div style={{ fontSize: 18, fontWeight: 800, marginTop: 24, textAlign: "center" }}>
          Happy saving!
        </div>
        <div style={{ color: "var(--primary)", fontSize: 13, fontWeight: 700, marginTop: 6, textAlign: "center" }}>
          — The Mrs CashBack Team
        </div>
      </div>
    </div>
  );
}

function Step({ n, Icon, title, body }) {
  return (
    <div style={{
      display: "flex", gap: 14,
      background: "var(--surface)", border: "1px solid var(--border)",
      borderRadius: 22, padding: 16, marginBottom: 12,
      boxShadow: "0 6px 18px -14px rgba(14, 34, 64, 0.2)",
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 18, background: "var(--navy)",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <span style={{ color: "#FFFFFF", fontSize: 16, fontWeight: 800 }}>{n}</span>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon size={18} color="var(--primary-dark)" />
          <span style={{ fontSize: 15, fontWeight: 800, color: "var(--navy)" }}>{title}</span>
        </div>
        <div style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
          {body}
        </div>
      </div>
    </div>
  );
}
